<!-- 
  SurveyWatcher.svelte
  This component watches for changes to the survey file and triggers updates
  without requiring a full page reload.
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { surveyStore } from '$lib/stores/surveyStore';
  import { interpretSurvey } from '$lib/utils/surveyInterpreter';
  import { page } from '$app/stores';
  import { goto } from '$app/navigation';
  
  // Configuration
  const POLL_INTERVAL = 1000; // Check every second
  let pollIntervalId: NodeJS.Timeout | null = null;
  let lastKnownTimestamp = 0;
  let lastKnownId = '';
  
  // Track if we're in development mode
  const isDev = import.meta.env.DEV;
  
  async function checkForChanges() {
    if (!isDev) return; // Only run in development mode
    
    try {
      // Add cache-busting query parameter
      const timestamp = new Date().getTime();
      const response = await fetch(`/content/refresh-signal.json?t=${timestamp}`, {
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });
      
      // Handle 404 gracefully - file might not exist yet
      if (response.status === 404) return;
      
      // Handle any other error
      if (!response.ok) {
        throw new Error(`Failed to fetch refresh signal: ${response.status}`);
      }
      
      try {
        // Get the response text first to check if it's valid JSON
        const responseText = await response.text();
        
        // Try to parse the response as JSON
        try {
          const data = JSON.parse(responseText);
          
          // If the timestamp or ID is newer than what we've seen before, update the survey
          const hasNewTimestamp = data.timestamp && data.timestamp > lastKnownTimestamp;
          const hasNewId = data.id && data.id !== lastKnownId;
          
          if (hasNewTimestamp || hasNewId) {
            if (data.timestamp) lastKnownTimestamp = data.timestamp;
            if (data.id) lastKnownId = data.id;
            
            // Update survey data
            await updateSurveyData();
          }
        } catch (jsonError) {
          // Silent error for invalid JSON
          return;
        }
      } catch (textError) {
        // Silent error for response text fetch
        return;
      }
    } catch (error) {
      // Only log errors that aren't 404-related
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (!errorMessage.includes('404') && !errorMessage.includes('not valid JSON')) {
        console.error('Error checking for survey updates:', errorMessage);
      }
    }
  }
  
  async function updateSurveyData() {
    try {
      // Fetch the new survey JSON with cache-busting
      const timestamp = new Date().getTime();
      const response = await fetch(`/content/survey.json?t=${timestamp}`, {
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch updated survey: ${response.status}`);
      }
      
      // Get the response text first to check if it's valid JSON
      const responseText = await response.text();
      
      try {
        // Parse the updated survey data
        const newSurveyData = JSON.parse(responseText);
        
        if (!newSurveyData) {
          throw new Error('Empty survey data received');
        }
        
        // Process the survey data
        const processedSurvey = interpretSurvey(newSurveyData);
        
        // Update the survey in the store
        surveyStore.updateSurvey(processedSurvey);
        
        // If we're on a specific question page, refresh that page to ensure we see the latest content
        if ($page.params.questionId) {
          // We're on a question page, refresh it without a hard reload
          const currentPath = $page.url.pathname;
          // Add a small delay to ensure the survey update event is processed first
          setTimeout(() => {
            goto(currentPath, { replaceState: true });
          }, 50);
        }
      } catch (jsonError) {
        console.error('Failed to parse survey JSON:', jsonError);
      }
    } catch (error) {
      console.error('Failed to update survey data:', error);
    }
  }
  
  onMount(() => {
    if (!isDev) return; // Only run in development mode
    
    // Initial check
    checkForChanges();
    
    // Start polling for changes
    pollIntervalId = setInterval(checkForChanges, POLL_INTERVAL);
  });
  
  onDestroy(() => {
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
    }
  });
</script>

<!-- No visible UI, just functionality --> 